/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/**
 * Firmware Download Service
 *
 * Handles fetching firmware metadata and signed URLs from GCS
 * via the get_plates_firmware callable function.
 */

import { FirebaseError } from 'firebase/app';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { userAsPromise } from '../../../../data/providers/userProvider';
import { FirmwareRequest, FirmwareVersion } from '../types/firmware-types';
import { ILogger } from '../types/imx-types';
import {
  ErrorCode,
  RecoveryAction,
  RecoveryInstruction,
  UpdaterError,
  WizardStep,
} from '../types/wizard-types';

// Signed URLs expire after 1 hour; refetch if older than 50 minutes for safety margin
const URL_EXPIRY_MS = 50 * 60 * 1000;

/**
 * Service for downloading firmware from cloud storage
 */
export class FirmwareDownloadService {
  private logger: ILogger;
  private cachedFirmware: FirmwareVersion | null = null;
  private cachedAt: number | null = null;

  constructor(logger: ILogger) {
    this.logger = logger;
  }

  /**
   * Fetch firmware metadata and signed URLs
   * @param version Optional specific version (defaults to latest)
   * @param currentStep The current wizard step (for error context)
   * @returns Firmware version info with signed URLs for all files
   * @throws UpdaterError with appropriate error code and recovery actions
   */
  async getFirmware(
    version?: string,
    currentStep?: WizardStep
  ): Promise<FirmwareVersion> {
    this.logger.info('Fetching firmware from cloud storage', {
      version: version || 'latest',
    });

    try {
      const user = await userAsPromise();
      const fn = httpsCallable<FirmwareRequest, FirmwareVersion>(
        user.services.functions,
        'getPlatesFirmware'
      );

      const result: HttpsCallableResult<FirmwareVersion> = await fn({
        version,
      });
      this.cachedFirmware = result.data;
      this.cachedAt = Date.now();

      this.logger.success('Firmware fetched successfully', {
        version: result.data.version,
        fileCount: Object.keys(result.data.files).length,
      });

      return result.data;
    } catch (error) {
      this.logger.error('Failed to fetch firmware', error);
      throw this.mapErrorToUpdaterError(error, version, currentStep);
    }
  }

  /**
   * Get a signed URL for a specific firmware file
   * @param filename The filename to get the URL for
   * @param currentStep The current wizard step (for error context)
   * @returns Signed URL for the file
   * @throws UpdaterError if firmware hasn't been fetched, URLs expired, or file not found
   */
  getFileUrl(filename: string, currentStep?: WizardStep): string {
    if (!this.cachedFirmware || !this.cachedAt) {
      throw new UpdaterError(
        'Firmware not loaded. Please retry the firmware fetch.',
        ErrorCode.FIRMWARE_FETCH_FAILED,
        currentStep || WizardStep.BOOTLOADER_TRANSFER,
        this.getRetryRecoveryActions()
      );
    }

    if (this.isUrlCacheExpired()) {
      this.logger.warning('Cached firmware URLs have expired, clearing cache');
      this.clearCache();
      throw new UpdaterError(
        'Firmware download URLs have expired. Please retry to fetch fresh URLs.',
        ErrorCode.FIRMWARE_FETCH_FAILED,
        currentStep || WizardStep.BOOTLOADER_TRANSFER,
        this.getRetryRecoveryActions(),
        'Signed URLs expire after 1 hour'
      );
    }

    const url = this.cachedFirmware.files[filename];
    if (!url) {
      throw new UpdaterError(
        `Firmware file "${filename}" not found in the current firmware version.`,
        ErrorCode.FIRMWARE_FILE_URL_FAILED,
        currentStep || WizardStep.BOOTLOADER_TRANSFER,
        this.getContactSupportRecoveryActions(),
        `File not found: ${filename}. Available files: ${Object.keys(
          this.cachedFirmware.files
        ).join(', ')}`
      );
    }

    return url;
  }

  /**
   * Check if firmware has been loaded
   */
  get isLoaded(): boolean {
    return this.cachedFirmware !== null;
  }

  /**
   * Get the currently cached firmware info
   */
  get currentFirmware(): FirmwareVersion | null {
    return this.cachedFirmware;
  }

  /**
   * Clear the cached firmware
   */
  clearCache(): void {
    this.cachedFirmware = null;
    this.cachedAt = null;
  }

  /**
   * Check if the cached URLs have expired
   */
  private isUrlCacheExpired(): boolean {
    if (!this.cachedAt) return true;
    return Date.now() - this.cachedAt > URL_EXPIRY_MS;
  }

  /**
   * Map Firebase/network errors to UpdaterError with appropriate codes
   */
  private mapErrorToUpdaterError(
    error: unknown,
    version?: string,
    currentStep?: WizardStep
  ): UpdaterError {
    const step = currentStep || WizardStep.BOOTLOADER_TRANSFER;

    // Handle Firebase HttpsError
    if (error instanceof Error && 'code' in error) {
      const firebaseError = error as FirebaseError;
      const code = firebaseError.code;

      if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
        return new UpdaterError(
          'You must be signed in to download firmware. Please sign in and try again.',
          ErrorCode.FIRMWARE_FETCH_UNAUTHENTICATED,
          step,
          [
            {
              action: RecoveryAction.START_OVER,
              label: 'Sign In',
              description:
                'Sign in to your account and restart the update process.',
              order: 1,
            },
          ]
        );
      }

      if (code === 'functions/not-found' || code === 'not-found') {
        const message = version
          ? `Firmware version "${version}" was not found.`
          : 'No firmware version is available.';
        return new UpdaterError(
          message,
          ErrorCode.FIRMWARE_VERSION_NOT_FOUND,
          step,
          this.getContactSupportRecoveryActions(),
          firebaseError.message
        );
      }

      if (code === 'functions/internal' || code === 'internal') {
        return new UpdaterError(
          'Failed to generate firmware download URLs. Please try again.',
          ErrorCode.FIRMWARE_FILE_URL_FAILED,
          step,
          this.getRetryRecoveryActions(),
          firebaseError.message
        );
      }
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return new UpdaterError(
        'Network error while fetching firmware. Please check your internet connection.',
        ErrorCode.FIRMWARE_FETCH_FAILED,
        step,
        [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry',
            description: 'Check your internet connection and try again.',
            order: 1,
          },
          ...this.getContactSupportRecoveryActions(),
        ]
      );
    }

    // Generic fallback
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    return new UpdaterError(
      'Failed to fetch firmware. Please try again.',
      ErrorCode.FIRMWARE_FETCH_FAILED,
      step,
      this.getRetryRecoveryActions(),
      errorMessage
    );
  }

  /**
   * Get standard retry recovery actions
   */
  private getRetryRecoveryActions(): RecoveryInstruction[] {
    return [
      {
        action: RecoveryAction.RETRY_STEP,
        label: 'Retry',
        description: 'Try fetching the firmware again.',
        order: 1,
      },
      {
        action: RecoveryAction.CONTACT_SUPPORT,
        label: 'Contact Support',
        description:
          'If the problem persists, contact Hawkin Dynamics support.',
        order: 2,
      },
    ];
  }

  /**
   * Get contact support recovery actions (for non-retryable errors)
   */
  private getContactSupportRecoveryActions(): RecoveryInstruction[] {
    return [
      {
        action: RecoveryAction.CONTACT_SUPPORT,
        label: 'Contact Support',
        description: 'Contact Hawkin Dynamics support for assistance.',
        order: 1,
      },
    ];
  }
}
